<template>
    <div v-if="ms.state.playing || ms.state.buffering || ms.currentSource.value" class="music-player">
        <div class="music-player-inner">
            <div class="music-player-controls">
                <button v-if="ms.state.currentTrack" :class="['music-player-btn music-player-btn--shuffle', {'music-player-btn--active': ms.state.shuffle}]" @click="ms.toggleShuffle()">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>
                </button>
                <button v-if="ms.state.currentTrack" class="music-player-btn" @click="ms.prevTrack()">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 20L9 12l10-8v16zM5 19V5h2v14H5z"/></svg>
                </button>
                <button class="music-player-btn music-player-btn--play" @click="ms.togglePlay()">
                    <svg v-if="ms.state.playing" width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                    <svg v-else width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                </button>
                <button v-if="ms.state.currentTrack" class="music-player-btn" @click="ms.nextTrack()">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M5 4l10 8-10 8V4zM19 5v14h-2V5h2z"/></svg>
                </button>
                <button v-if="ms.state.currentTrack" :class="['music-player-btn music-player-btn--repeat', {'music-player-btn--active': ms.state.repeat !== 'off'}]" @click="ms.toggleRepeat()">
                    <svg v-if="ms.state.repeat === 'one'" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/><text x="12" y="14" fill="currentColor" stroke="none" font-size="8" font-weight="700" text-anchor="middle">1</text></svg>
                    <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                </button>
            </div>

            <div :class="['music-player-source', {'music-player-source--seekable': hasProgress}]" @click="$emit('toggle-now-playing')">
                <div :class="['music-player-cover', {'music-player-cover--placeholder': !coverUrl}]">
                    <img v-if="coverUrl" :src="coverUrl" @error="coverError = true">
                    <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                </div>
                <div class="music-player-info">
                    <div class="music-player-name">{{ ms.displayName.value }}</div>
                    <div class="music-player-sub">
                        <template v-if="ms.state.buffering">{{ $t('common.music.buffering') }}</template>
                        <template v-else-if="ms.state.error">{{ $t('common.music.' + ms.state.error) }}</template>
                        <template v-else-if="cycleTexts.length">
                            <transition name="music-sub-fade" mode="out-in">
                                <span v-if="cycleTexts[subCycleIndex] === null" key="progress">{{ formatTime(ms.state.currentTime) }} / {{ formatTime(ms.state.duration) }}</span>
                                <span v-else :key="subCycleIndex">{{ cycleTexts[subCycleIndex] }}</span>
                            </transition>
                        </template>
                        <template v-else>{{ ms.displaySubtext.value }}</template>
                    </div>
                </div>
                <div v-if="hasProgress" class="music-player-progress"
                     @click.stop
                     @mousedown.stop="onProgressDown($event)"
                     @touchstart.stop.prevent="onProgressTouch($event)">
                    <div class="music-player-progress-fill" :style="{width: progressPercent + '%'}"></div>
                </div>
            </div>

            <div class="music-player-actions">
                <button v-if="ms.state.currentTrack" :class="['music-player-btn', {'music-player-btn--active': isTrackFavorite}]" @click="toggleFavorite">
                    <svg width="18" height="18" viewBox="0 0 24 24" :fill="isTrackFavorite ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                </button>
                <button class="music-player-btn" @click="ms.toggleMute()">
                    <svg v-if="ms.state.muted || ms.state.volume === 0" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                    <svg v-else-if="ms.state.volume < 0.5" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                    <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                </button>
                <input type="range" class="music-volume-slider" min="0" max="1" step="0.01"
                       :value="ms.state.volume" :style="volumeGradient" @input="onVolume($event)">
            </div>
        </div>
        <div class="music-player-compact-actions">
            <button class="music-player-cbtn" @click="ms.togglePlay()">
                <svg v-if="ms.state.playing" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            </button>
            <div v-if="ms.state.currentTrack" class="music-player-cmenu-wrap">
                <button class="music-player-cbtn" @click.stop="compactMenu = !compactMenu">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
                </button>
                <div v-if="compactMenu" class="music-player-cmenu" @click.stop>
                    <button :class="['music-player-cmenu-item', {'music-player-cmenu-item--active': ms.state.shuffle}]" @click="ms.toggleShuffle(); compactMenu = false">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/></svg>
                        <span>{{ $t('common.music.shuffle') }}</span>
                    </button>
                    <button :class="['music-player-cmenu-item', {'music-player-cmenu-item--active': ms.state.repeat !== 'off'}]" @click="ms.toggleRepeat(); compactMenu = false">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                        <span>{{ repeatLabel }}</span>
                    </button>
                    <button :class="['music-player-cmenu-item', {'music-player-cmenu-item--active': isTrackFavorite}]" @click="toggleFavorite(); compactMenu = false">
                        <svg width="15" height="15" viewBox="0 0 24 24" :fill="isTrackFavorite ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                        <span>{{ $t('common.music.favorite') }}</span>
                    </button>
                </div>
            </div>
            <div class="music-player-cvol-wrap">
                <button class="music-player-cbtn" @click.stop="volumePopup = !volumePopup">
                    <svg v-if="ms.state.muted || ms.state.volume === 0" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                    <svg v-else-if="ms.state.volume < 0.5" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                </button>
                <div v-if="volumePopup" class="music-player-cvol" @click.stop>
                    <div class="music-player-cvol-track"
                         @mousedown.stop="onVolBarDown($event)"
                         @touchstart.stop.prevent="onVolBarTouch($event)">
                        <div class="music-player-cvol-fill" :style="{height: volPercent + '%'}"></div>
                        <div class="music-player-cvol-thumb" :style="{bottom: volPercent + '%'}"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import musicService from './musicService'

export default {
    name: 'MusicPlayerBar',
    emits: ['toggle-now-playing'],
    data() {
        return {
            coverError: false,
            compactMenu: false,
            volumePopup: false,
            subCycleIndex: 0,
        }
    },
    created() {
        this._progressRect = null
        this._onProgressMove = (e) => {
            this.seekFromX(e.clientX)
        }
        this._onProgressUp = () => {
            document.removeEventListener('mousemove', this._onProgressMove)
            document.removeEventListener('mouseup', this._onProgressUp)
        }
        this._onTouchMove = (e) => {
            if (e.touches.length) this.seekFromX(e.touches[0].clientX)
        }
        this._onTouchEnd = () => {
            document.removeEventListener('touchmove', this._onTouchMove)
            document.removeEventListener('touchend', this._onTouchEnd)
        }
        this._volRect = null
        this._onVolMove = (e) => {
            this.volFromY(e.clientY)
        }
        this._onVolUp = () => {
            document.removeEventListener('mousemove', this._onVolMove)
            document.removeEventListener('mouseup', this._onVolUp)
        }
        this._onVolTouchMove = (e) => {
            if (e.touches.length) this.volFromY(e.touches[0].clientY)
        }
        this._onVolTouchEnd = () => {
            document.removeEventListener('touchmove', this._onVolTouchMove)
            document.removeEventListener('touchend', this._onVolTouchEnd)
        }
        this._onDocClick = (e) => {
            if (this.compactMenu && !e.target.closest('.music-player-cmenu-wrap')) {
                this.compactMenu = false
            }
            if (this.volumePopup && !e.target.closest('.music-player-cvol-wrap')) {
                this.volumePopup = false
            }
        }
    },
    computed: {
        ms() { return musicService },
        progressPercent() {
            if (!musicService.state.duration) return 0
            return (musicService.state.currentTime / musicService.state.duration) * 100
        },
        hasProgress() {
            return musicService.state.currentTrack && musicService.state.duration > 0
        },
        coverUrl() {
            if (this.coverError) return null
            if (musicService.state.currentStation?.favicon) return musicService.state.currentStation.favicon
            if (musicService.state.currentTrack?.hasCover && musicService.state.currentTrack.thumb) return musicService.state.currentTrack.thumb.small
            return null
        },
        isTrackFavorite() {
            if (!musicService.state.currentTrack) return false
            return musicService.isFavoriteTrack(musicService.state.currentTrack.id)
        },
        volumeGradient() {
            const pct = (musicService.state.muted ? 0 : musicService.state.volume) * 100
            return { background: `linear-gradient(to right, var(--music-accent) ${pct}%, rgba(128,128,128,0.2) ${pct}%)` }
        },
        repeatLabel() {
            if (musicService.state.repeat === 'one') return this.$t('common.music.repeatOne')
            if (musicService.state.repeat === 'all') return this.$t('common.music.repeatAll')
            return this.$t('common.music.repeat')
        },
        volPercent() {
            return (musicService.state.muted ? 0 : musicService.state.volume) * 100
        },
        cycleTexts() {
            const track = musicService.state.currentTrack
            if (!track) return []
            const texts = []
            if (track.duration > 0) texts.push(null)
            if (track.artist) texts.push(track.artist)
            const albumParts = []
            if (track.tags?.album) albumParts.push(track.tags.album)
            if (track.tags?.year) albumParts.push(track.tags.year)
            if (albumParts.length) texts.push(albumParts.join(' \u2013 '))
            return texts
        },
    },
    methods: {
        onVolume(e) {
            musicService.setVolume(parseFloat(e.target.value))
        },
        toggleFavorite() {
            const id = musicService.state.currentTrack?.id
            if (!id) return
            if (musicService.isFavoriteTrack(id)) musicService.removeFavoriteTrack(id)
            else musicService.addFavoriteTrack(id)
        },
        onVolBarDown(e) {
            this._volRect = e.currentTarget.getBoundingClientRect()
            this.volFromY(e.clientY)
            document.addEventListener('mousemove', this._onVolMove)
            document.addEventListener('mouseup', this._onVolUp)
        },
        onVolBarTouch(e) {
            this._volRect = e.currentTarget.getBoundingClientRect()
            if (e.touches.length) this.volFromY(e.touches[0].clientY)
            document.addEventListener('touchmove', this._onVolTouchMove, { passive: true })
            document.addEventListener('touchend', this._onVolTouchEnd)
        },
        volFromY(clientY) {
            if (!this._volRect) return
            const rect = this._volRect
            const pct = 1 - Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))
            musicService.setVolume(pct)
        },
        onProgressDown(e) {
            const bar = e.currentTarget
            this._progressRect = bar.getBoundingClientRect()
            this.seekFromX(e.clientX)
            document.addEventListener('mousemove', this._onProgressMove)
            document.addEventListener('mouseup', this._onProgressUp)
        },
        onProgressTouch(e) {
            const bar = e.currentTarget
            this._progressRect = bar.getBoundingClientRect()
            if (e.touches.length) this.seekFromX(e.touches[0].clientX)
            document.addEventListener('touchmove', this._onTouchMove, { passive: true })
            document.addEventListener('touchend', this._onTouchEnd)
        },
        seekFromX(clientX) {
            if (!this._progressRect) return
            const rect = this._progressRect
            const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
            musicService.seek(pct * musicService.state.duration)
        },
        formatTime(sec) {
            if (!sec || !isFinite(sec)) return '0:00'
            const m = Math.floor(sec / 60)
            const s = Math.floor(sec % 60)
            return m + ':' + (s < 10 ? '0' : '') + s
        },
        _restartCycleTimer() {
            clearInterval(this._cycleTimer)
            this._cycleTimer = null
            if (this.cycleTexts.length > 1) {
                this._cycleTimer = setInterval(() => {
                    this.subCycleIndex = (this.subCycleIndex + 1) % this.cycleTexts.length
                }, 3000)
            }
        },
    },
    watch: {
        'ms.state.currentStation'() { this.coverError = false },
        'ms.state.currentTrack'() {
            this.coverError = false
            this.subCycleIndex = 0
            this._restartCycleTimer()
        },
        cycleTexts(texts) {
            if (this.subCycleIndex >= texts.length) this.subCycleIndex = 0
            this._restartCycleTimer()
        },
    },
    mounted() {
        document.addEventListener('mousedown', this._onDocClick)
        this._restartCycleTimer()
    },
    beforeUnmount() {
        clearInterval(this._cycleTimer)
        document.removeEventListener('mousemove', this._onProgressMove)
        document.removeEventListener('mouseup', this._onProgressUp)
        document.removeEventListener('touchmove', this._onTouchMove)
        document.removeEventListener('touchend', this._onTouchEnd)
        document.removeEventListener('mousemove', this._onVolMove)
        document.removeEventListener('mouseup', this._onVolUp)
        document.removeEventListener('touchmove', this._onVolTouchMove)
        document.removeEventListener('touchend', this._onVolTouchEnd)
        document.removeEventListener('mousedown', this._onDocClick)
    },
}
</script>
